"use client";
import React, { useState, useEffect, useCallback } from "react";
import { ArrowLeft, ArrowRight } from "lucide-react";
import Image from "next/image";
import { heroData } from "../data/heroData";
import { AnimatePresence, motion } from "framer-motion";

const Hero = () => {
  const [currentIndex, setCurrentIndex] = useState(0);
  const [animate, setAnimate] = useState(true);
  const [autoScrollEnabled, setAutoScrollEnabled] = useState(true);

  // Memoized navigation handlers
  const handleNext = useCallback(() => {
    setAutoScrollEnabled(false); // Disable auto-scroll on manual navigation
    setAnimate(true);
    setCurrentIndex((prevIndex) =>
      prevIndex === heroData.length - 1 ? 0 : prevIndex + 1
    );
    // Re-enable auto-scroll after a delay if user doesn't interact further
    const timer = setTimeout(() => setAutoScrollEnabled(true), 1000);
    return () => clearTimeout(timer);
  }, []);

  const handlePrev = useCallback(() => {
    setAutoScrollEnabled(false); // Disable auto-scroll on manual navigation
    setAnimate(true);
    setCurrentIndex((prevIndex) =>
      prevIndex === 0 ? heroData.length - 1 : prevIndex - 1
    );
    // Re-enable auto-scroll after a delay if user doesn't interact further
    const timer = setTimeout(() => setAutoScrollEnabled(true), 1000);
    return () => clearTimeout(timer);
  }, []);

  // Animation timeout effect
  useEffect(() => {
    const timer = setTimeout(() => setAnimate(false), 500);
    return () => clearTimeout(timer);
  }, []);

  useEffect(() => {
    if (animate) {
      const timer = setTimeout(() => setAnimate(false), 500);
      return () => clearTimeout(timer);
    }
  }, [animate]);

  // Auto-scroll effect
  useEffect(() => {
    if (!autoScrollEnabled) return;

    const interval = setInterval(() => {
      handleNext();
    }, 2000); // 2 seconds interval + 1sec delay 

    return () => clearInterval(interval);
  }, [autoScrollEnabled, handleNext]);

  const currentData = heroData[currentIndex];

  const handleKnowMoreClick = () => {
    const targetSection = document.getElementById(currentData.id);
    if (targetSection) {
      targetSection.scrollIntoView({ behavior: "smooth" });
    }
  };

  const handleFreeConsultationClick = () => {
    const contactSection = document.getElementById("contact");
    if (contactSection) {
      contactSection.scrollIntoView({ behavior: "smooth" });
    }
  };

  return (
    <>
      <div className="flex flex-col gap-2 mt-[70px] h-[86vh] mb-[4rem] mx-[1.5rem] sm:mx-[3rem] ">
        {/* Image Section */}
        <div className="h-[90vh] relative overflow-hidden">
          {/* Image with animation */}
          <div className="relative w-full h-[90vh] overflow-hidden">
            <AnimatePresence>
              {heroData.map(
                (data, index) =>
                  index === currentIndex && (
                    <motion.div
                      key={index}
                      initial={{ opacity: 0, y: -100 }}
                      animate={{ opacity: 1, y: 0 }}
                      exit={{ opacity: 0, y: 100 }}
                      transition={{ duration: 0.5 }}
                      className="absolute inset-0"
                      style={{ zIndex: index === currentIndex ? 1 : 0 }}
                    >
                      <Image
                        src={currentData.imageUrl}
                        alt="Hero Image"
                        fill
                        priority
                        quality={75}
                        loading="eager"
                        fetchPriority="high"
                        placeholder="blur"
                        blurDataURL="data:image/png;base64,..."
                        className="object-cover object-center"
                        sizes="100vw"
                      />
                    </motion.div>
                  )
              )}
            </AnimatePresence>
          </div>

          {/* Right Section for Mobile */}
          <div className=" absolute inset-0 flex flex-col gap-8 justify-center items-start p-5 sm:p-10 bg-black bg-opacity-20 z-10">
            <div className="flex flex-col lg:flex-row lg:items-center lg:gap-4 items-start">
              <div
                className={`text-white text-title-size2 font-thin font-robo leading-[0.9] ${
                  animate ? "animate-slide-in" : ""
                }`}
              >
                {currentData.title.mainTitle}
              </div>
              <div
                className={`text-white font-weight1 text-title-size1 leading-[0.9] relative `}
              >
                Solar
                {/* Animated Orange Line */}
                <div
                  key={currentData.id}
                  className="absolute -bottom-1 left-0 w-0 h-[1px] bg-[#ffffff] animate-line"
                ></div>
              </div>
            </div>

            {/* Navigation Arrows */}
            <div className="flex flex-row gap-4 mt-4">
              <div
                className="w-10 h-10 shadow-sm bg-hex flex items-center justify-center cursor-pointer hover:bg-quad transition-colors hover:scale-90"
                onClick={handlePrev}
              >
                <ArrowLeft size={20} />
              </div>
              <div
                className="w-10 h-10 shadow-sm bg-hex flex items-center justify-center cursor-pointer hover:bg-quad transition-colors hover:scale-90"
                onClick={handleNext}
              >
                <ArrowRight size={20} />
              </div>
            </div>

            {/* Know More Button */}
            <div
              className="bg-pri py-1 px-4 rounded-md text-color4 w-fit flex items-center gap-1 cursor-pointer hover:bg-pri-dark transition-colors mt-4"
              onClick={handleKnowMoreClick}
            >
              Know more <ArrowRight size={16} />
            </div>
          </div>
          <div
            className="z-10 flex flex-row gap-3 absolute bottom-0 right-0 bg-white tracking-[.18em] py-2 px-4 text-color3 text-desc-size3 font-robo cursor-pointer hover:bg-pri hover:text-white"
            onClick={handleFreeConsultationClick}
          >
            <span>GET FREE CONSULTATION</span> <ArrowRight size={16} />
          </div>
        </div>
      </div>
    </>
  );
};

export default Hero;